import { useCallback, useEffect, useState } from "react";
import { IpRestS } from "../../rest/ipRest-service";
import { RestS } from "../../rest/rest-service";
import { getLocalUser } from "../functions/utilities";

interface UseZohoConnectOptions {
  // Short code identifying where the connect flow was started from, so
  // finance-api's OAuth callback knows where to send the browser back to.
  from: string;
  // Overrides getLocalUser().companyId — e.g. Underwriting scopes this to
  // the specific application's company (extraFormDetails.companyId), which
  // can differ from the logged-in user's own company for Agency-role users.
  companyId?: number;
}

export function useZohoConnect({ from, companyId }: UseZohoConnectOptions) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // window.location.href never resolves/rejects the promise chain below,
    // so if the browser restores this page from bfcache (e.g. the user hit
    // Back instead of finishing the redirect) isConnecting would otherwise
    // stay stuck true forever.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setIsConnecting(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const connect = useCallback(() => {
    setError("");
    setIsConnecting(true);

    IpRestS.getIpByName("Zoho")
      .then((ip) => {
        const user = getLocalUser();
        if (!ip?._id || !user) throw new Error("missing ipId/user");
        return RestS.getZohoAuthUrl(ip._id, ip.companyName ?? "Zoho Books", from, companyId);
      })
      .then((res) => {
        if (!res?.success || !res?.data?.authUrl) throw new Error("missing authUrl");
        window.location.href = res.data.authUrl;
      })
      .catch(() => {
        setError("Could not start Zoho connection. Please try again.");
        setIsConnecting(false);
      });
  }, [from, companyId]);

  return { connect, isConnecting, error };
}
